import { EventType } from "@/analytics/analytics-events";
import { SelectDateFilterPresetEventProperties } from "@/analytics/project-filter-events-properties";
import { selectLastCaptureDate } from "@/store/lightweight-elements-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  DatePicker,
  FaroButton,
  FaroChipFilter,
  FaroChipToggle,
  FaroMenu,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Divider } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState, type JSX } from "react";

type DateRangeFilterProps = {
  /** The currently selected start date */
  selectedStartDate?: Date;

  /** The currently selected end date */
  selectedEndDate?: Date;

  /** Callback to change the date range */
  onDateRangeChange(start?: Date, end?: Date): void;
};

const TODAY = new Date();

export enum DateFilterPreset {
  /** Showing only captures taken the last day */
  lastCapture = "Last capture",
  /** Showing only captures taken this week */
  thisWeek = "This week",
  /** Showing only captures taken this month */
  thisMonth = "This month",
}

/** @returns A chip with a popup that can be used to select a date range */
export function DateRangeFilter({
  selectedStartDate,
  selectedEndDate,
  onDateRangeChange,
}: DateRangeFilterProps): JSX.Element {
  const lastCaptureDate = useAppSelector(selectLastCaptureDate);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement>();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const setDateFilterPreset = useCallback(
    (preset: DateFilterPreset) => {
      Analytics.track<SelectDateFilterPresetEventProperties>(
        EventType.selectDateFilterPreset,
        { preset },
      );
      if (preset === DateFilterPreset.lastCapture && lastCaptureDate) {
        onDateRangeChange(lastCaptureDate, lastCaptureDate);
        return;
      }
      onDateRangeChange(
        DateTime.now().startOf(toString(preset)).toJSDate(),
        DateTime.now().endOf("day").toJSDate(),
      );
    },
    [onDateRangeChange, lastCaptureDate],
  );

  const hasActiveFilter = !!selectedStartDate || !!selectedEndDate;

  const { isLastCapture, isThisWeek, isThisMonth } = useIsPresetSelected(
    selectedStartDate,
    selectedEndDate,
    lastCaptureDate,
  );

  const onFromDateChange = useCallback(
    (date: Date | undefined) => {
      if (shouldSetLastCaptureDate(date, selectedEndDate, lastCaptureDate)) {
        onDateRangeChange(date, lastCaptureDate);
        return;
      }
      onDateRangeChange(date, selectedEndDate);
    },
    [onDateRangeChange, selectedEndDate, lastCaptureDate],
  );

  const onToDateChange = useCallback(
    (date: Date | undefined) => {
      onDateRangeChange(selectedStartDate, date);
    },
    [onDateRangeChange, selectedStartDate],
  );

  const lastCaptureClicked = useCallback(() => {
    if (isLastCapture) {
      onDateRangeChange();
    } else {
      setDateFilterPreset(DateFilterPreset.lastCapture);
    }
  }, [isLastCapture, setDateFilterPreset, onDateRangeChange]);

  const thisWeekClicked = useCallback(() => {
    if (isThisWeek) {
      onDateRangeChange();
    } else {
      setDateFilterPreset(DateFilterPreset.thisWeek);
    }
  }, [isThisWeek, setDateFilterPreset, onDateRangeChange]);

  const thisMonthClicked = useCallback(() => {
    if (isThisMonth) {
      onDateRangeChange();
    } else {
      setDateFilterPreset(DateFilterPreset.thisMonth);
    }
  }, [isThisMonth, setDateFilterPreset, onDateRangeChange]);

  const clearAllClicked = useCallback(() => {
    Analytics.track(EventType.resetDateFilter);
    onDateRangeChange();
  }, [onDateRangeChange]);

  return (
    <Box ref={setAnchorEl} component="div">
      <FaroChipFilter
        label="Time of capture"
        selected={isPopoverOpen || hasActiveFilter}
        showCheckmark={hasActiveFilter}
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
      />

      {anchorEl && (
        <FaroMenu
          anchorEl={anchorEl}
          open={isPopoverOpen}
          onClose={() => setIsPopoverOpen(false)}
          sx={{
            width: "500px",
            mt: 0.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "space-around", m: 1.5 }}
          >
            <FaroChipToggle
              label="Last capture"
              selected={isLastCapture}
              onClick={lastCaptureClicked}
            />
            <FaroChipToggle
              label="This week"
              selected={isThisWeek}
              onClick={thisWeekClicked}
            />
            <FaroChipToggle
              label="This month"
              selected={isThisMonth}
              onClick={thisMonthClicked}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "space-around", my: 1.5, mx: 2 }}
          >
            <DatePicker
              label="From"
              date={selectedStartDate}
              maxDate={selectedEndDate ?? TODAY}
              onChange={onFromDateChange}
            />
            <DatePicker
              label="To"
              date={selectedEndDate}
              minDate={selectedStartDate}
              maxDate={TODAY}
              onChange={onToDateChange}
            />
          </Stack>

          <Divider />

          <FaroButton variant="ghost" onClick={clearAllClicked}>
            Clear all
          </FaroButton>
        </FaroMenu>
      )}
    </Box>
  );
}

type SelectedPresets = {
  isLastCapture: boolean;
  isThisWeek: boolean;
  isThisMonth: boolean;
};

/**
 *
 * @param selectedStartDate The currently selected start date
 * @param selectedEndDate The currently selected end date
 * @param lastCaptureDate The last capture date
 * @returns whether any preset is selected
 */
function useIsPresetSelected(
  selectedStartDate: Date | undefined,
  selectedEndDate: Date | undefined,
  lastCaptureDate: Date | undefined,
): SelectedPresets {
  return useMemo(() => {
    const allFalse = {
      isLastCapture: false,
      isThisWeek: false,
      isThisMonth: false,
    };

    if (!selectedStartDate || !selectedEndDate) {
      return allFalse;
    }

    const lastCaptureDateTime = lastCaptureDate
      ? DateTime.fromJSDate(lastCaptureDate)
      : DateTime.now().endOf("day");

    const endOfDay = DateTime.now().endOf("day");
    const startOfWeek = DateTime.now().startOf("week");
    const startOfMonth = DateTime.now().startOf("month");

    const start = DateTime.fromJSDate(selectedStartDate);
    const end = DateTime.fromJSDate(selectedEndDate);

    if (
      end.hasSame(lastCaptureDateTime, "day") &&
      start.hasSame(lastCaptureDateTime, "day")
    ) {
      return { ...allFalse, isLastCapture: true };
    }

    if (end.hasSame(endOfDay, "day")) {
      if (start.hasSame(startOfWeek, "day")) {
        return { ...allFalse, isThisWeek: true };
      }

      if (start.hasSame(startOfMonth, "day")) {
        return { ...allFalse, isThisMonth: true };
      }
    }

    return allFalse;
  }, [selectedStartDate, selectedEndDate, lastCaptureDate]);
}

/**
 * When setting the 'from' date, this function checks whether the 'to' date should be set to the last capture date.
 *
 * @param date The 'from' date being set
 * @param selectedEndDate The currently selected 'to' date
 * @param lastCaptureDate The date of the last capture in the project
 * @returns whether the 'to' date should be set to the last capture date.
 */
function shouldSetLastCaptureDate(
  date: Date | undefined,
  selectedEndDate: Date | undefined,
  lastCaptureDate: Date | undefined,
): boolean {
  return (
    !!date && !selectedEndDate && !!lastCaptureDate && date < lastCaptureDate
  );
}

function toString(preset: DateFilterPreset): "day" | "week" | "month" {
  switch (preset) {
    case DateFilterPreset.lastCapture:
      return "day";
    case DateFilterPreset.thisWeek:
      return "week";
    case DateFilterPreset.thisMonth:
      return "month";
  }
}
